import { createElement } from "react";
import { createObj } from "../fable_modules/fable-library-js.4.28.0/Util.js";
import { collect, forAll, empty, singleton, append, delay, toList } from "../fable_modules/fable-library-js.4.28.0/Seq.js";
import { reactApi } from "../fable_modules/Feliz.2.9.0/Interop.fs.js";
import { isEmpty, ofArray, singleton as singleton_1 } from "../fable_modules/fable-library-js.4.28.0/List.js";
import { isDigit } from "../fable_modules/fable-library-js.4.28.0/Char.js";
import { min, max } from "../fable_modules/fable-library-js.4.28.0/Double.js";
import { tryParse } from "../fable_modules/fable-library-js.4.28.0/Int32.js";
import { FSharpRef } from "../fable_modules/fable-library-js.4.28.0/Types.js";
import { PropHelpers_createOnKey } from "../fable_modules/Feliz.2.9.0/Properties.fs.js";
import { key_escape } from "../fable_modules/Feliz.2.9.0/Key.fs.js";
import { t } from "../Lang.js";
import { Help_title, Common_cancel, Common_delete } from "../Shared/Tr.js";
import { defaultOf } from "../fable_modules/fable-library-js.4.28.0/Util.js";

/**
 * Text input with label, optional required indicator, and validation error styling.
 * When showError is true, adds red border + shake animation.
 * shakeKey should change each time the shake should replay (e.g. a counter).
 */
export function formInput(label, value, placeholder, onChange, disabled, required, showError, shakeKey) {
    let elems_1, elems;
    const inputClass = showError ? "form-input form-input-error" : "form-input";
    return createElement("div", createObj(ofArray([["className", showError ? "form-group shake" : "form-group"], ["key", showError ? (`shake-${label}-${shakeKey}`) : (`field-${label}`)], (elems_1 = [createElement("label", createObj(singleton_1((elems = toList(delay(() => append(singleton(label), delay(() => (required ? singleton(createElement("span", {
        className: "form-required",
        children: "*",
    })) : empty()))))), ["children", reactApi.Children.toArray(Array.from(elems))])))), createElement("input", {
        className: inputClass,
        type: "text",
        placeholder: placeholder,
        value: value,
        onChange: (ev) => {
            onChange(ev.target.value);
        },
        disabled: disabled,
    })], ["children", reactApi.Children.toArray(Array.from(elems_1))])])));
}

/**
 * Textarea with label, optional required indicator, and validation error styling.
 */
export function formTextarea(label, value, rows, onChange, disabled, required, showError, shakeKey) {
    let elems_1, elems;
    const inputClass = showError ? "form-input form-input-error" : "form-input";
    return createElement("div", createObj(ofArray([["className", showError ? "form-group shake" : "form-group"], ["key", showError ? (`shake-${label}-${shakeKey}`) : (`field-${label}`)], (elems_1 = [createElement("label", createObj(singleton_1((elems = toList(delay(() => append(singleton(label), delay(() => (required ? singleton(createElement("span", {
        className: "form-required",
        children: "*",
    })) : empty()))))), ["children", reactApi.Children.toArray(Array.from(elems))])))), createElement("textarea", {
        className: inputClass,
        rows: rows,
        value: value,
        onChange: (ev) => {
            onChange(ev.target.value);
        },
        disabled: disabled,
    })], ["children", reactApi.Children.toArray(Array.from(elems_1))])])));
}

/**
 * Numeric text input that doesn't fight the user.
 * Uses inputMode="numeric" instead of type="number" to avoid browser min/max blocking.
 * Uses defaultValue (uncontrolled) so clearing and retyping works freely.
 * Non-digit characters are stripped on input. Empty string dispatches 0 (clamped to min).
 */
export function numericInput(defaultVal, onValid, clampMin, clampMax) {
    return createElement("input", {
        className: "form-input",
        inputMode: "numeric",
        defaultValue: defaultVal,
        onInput: (e) => {
            const el = e.target;
            const v = el.value;
            if ((v !== "") && !forAll(isDigit, v.split(""))) {
                el.value = "";
                onValid(max(clampMin, 0));
            }
            else if (v === "") {
                onValid(max(clampMin, 0));
            }
            else {
                let matchValue;
                let outArg = 0;
                matchValue = [tryParse(v, 511, false, 32, new FSharpRef(() => outArg, (v_1) => {
                    outArg = (v_1 | 0);
                })), outArg];
                if (matchValue[0]) {
                    onValid(max(clampMin, min(clampMax, matchValue[1])));
                }
            }
        },
    });
}

/**
 * Confirmation dialog overlay (ESC or click backdrop to cancel)
 */
export function confirmDialog(message, onConfirm, onCancel) {
    let elems_2, elems_1, elems;
    return createElement("div", createObj(ofArray([["className", "dialog-overlay"], ["tabIndex", -1], ["ref", (el) => {
        if (!(el == null)) {
            el.focus();
        }
    }], ["onKeyDown", (ev) => {
        PropHelpers_createOnKey(key_escape, (_arg) => {
            onCancel();
        }, ev);
    }], ["onClick", (_arg_1) => {
        onCancel();
    }], (elems_2 = [createElement("div", createObj(ofArray([["className", "dialog"], ["onClick", (e) => {
        e.stopPropagation();
    }], (elems_1 = [createElement("p", {
        children: message,
    }), createElement("div", createObj(ofArray([["className", "flex gap-sm mt-2"], (elems = [createElement("button", {
        className: "btn btn-danger",
        children: t(Common_delete),
        onClick: (_arg_2) => {
            onConfirm();
        },
    }), createElement("button", {
        className: "btn btn-secondary",
        children: t(Common_cancel),
        onClick: (_arg_3) => {
            onCancel();
        },
    })], ["children", reactApi.Children.toArray(Array.from(elems))])])))], ["children", reactApi.Children.toArray(Array.from(elems_1))])])))], ["children", reactApi.Children.toArray(Array.from(elems_2))])])));
}

/**
 * Badge for tags display
 */
export function badge(text) {
    return createElement("span", {
        className: "badge",
        children: text,
    });
}

/**
 * Contextual help icon (?) that opens the help panel at a specific chapter and optional anchor.
 * Takes a generic onClick callback to avoid circular dependency with Messages.
 */
export function helpIcon(onClick) {
    return createElement("button", {
        className: "help-icon-btn",
        title: t(Help_title),
        onClick: (e) => {
            e.stopPropagation();
            onClick();
        },
        children: "?",
    });
}

/**
 * Toast notification container — renders a fixed-position stack of toasts.
 * Takes a list of (id, levelClass, message) tuples and a dismiss callback to avoid
 * dependency on AppModel/AppMessages (Components compiles before those modules).
 */
export function toastContainer(toasts, onDismiss) {
    let elems_1;
    if (isEmpty(toasts)) {
        return defaultOf();
    }
    else {
        return createElement("div", createObj(ofArray([["className", "toast-container"], (elems_1 = toList(delay(() => collect((matchValue) => {
            let elems;
            const id = matchValue[0];
            return singleton(createElement("div", createObj(ofArray([["key", id], ["className", matchValue[1]], (elems = [createElement("span", {
                className: "toast-message",
                children: matchValue[2],
            }), createElement("button", {
                className: "toast-dismiss",
                children: "×",
                onClick: (_arg) => {
                    onDismiss(id);
                },
            })], ["children", reactApi.Children.toArray(Array.from(elems))])]))));
        }, toasts))), ["children", reactApi.Children.toArray(Array.from(elems_1))])])));
    }
}

/**
 * Contextual help tooltip — (?) icon that shows a popup on hover/focus
 */
export function helpTooltip(text) {
    let elems;
    return createElement("span", createObj(ofArray([["className", "tooltip-trigger"], ["tabIndex", 0], (elems = [createElement("span", {
        className: "tooltip-icon",
        children: "?",
    }), createElement("span", {
        className: "tooltip-content",
        children: text,
    })], ["children", reactApi.Children.toArray(Array.from(elems))])])));
}

/**
 * Section header with optional action button
 */
export function sectionHeader(title, actionLabel, onAction) {
    let elems;
    return createElement("div", createObj(ofArray([["className", "flex items-center justify-between mb-1"], (elems = toList(delay(() => append(singleton(createElement("h3", {
        children: title,
    })), delay(() => {
        let action, label;
        return (actionLabel != null) ? ((onAction != null) ? ((action = onAction, (label = actionLabel, singleton(createElement("button", {
            className: "btn btn-sm btn-primary",
            children: label,
            onClick: (_arg) => {
                action();
            },
        }))))) : singleton(defaultOf())) : singleton(defaultOf());
    })))), ["children", reactApi.Children.toArray(Array.from(elems))])])));
}

